import { AlpineComponent } from "alpinejs";
import {
	client,
	type RegisterNewCustomerErrors,
	type RegisterNewCustomerResponses,
	type ValidationFailure,
} from "../api";

interface SubmissionError {
	errorMessage: string;
}

interface CountryOption {
	text: string;
	value: string;
}

interface RoleOption {
	text: string;
	value: string;
}

interface BusinessAreaOption {
	text: string;
	value: string;
}

interface RegisterFormConfig {
	businessAreas?: BusinessAreaOption[];
	customerRoles?: Record<string, RoleOption[]>;
	countries?: CountryOption[];
	submitText?: string;
	submittingText?: string;
	validationFailedText?: string;
	genericErrorText?: string;
}

type RegisterFormComponent = AlpineComponent<{
	applicationSubmitted: boolean;
	applicationError: boolean;
	isSubmitting: boolean;
	submissionErrors: SubmissionError[];
	termsAccepted: boolean;
	gdprAccepted: boolean;
	countries: CountryOption[];
	businessAreas: BusinessAreaOption[];
	customerRoles: Record<string, RoleOption[]>;
	submitText: string;
	submittingText: string;
	validationFailedText: string;
	genericErrorText: string;
	newCustomer: {
		email: string;
		selectedbusinessarea: string;
		selectedcustomerrole: string;
		companyname: string;
		companyvatnumber: string;
		postaladdress1: string;
		postaladdress2: string;
		postalzipcode: string;
		postalcity: string;
		postalcountry: string;
		usedifferentdeliveryaddress: boolean;
		deliveryaddress1: string;
		deliveryaddress2: string;
		deliveryzipcode: string;
		deliverycity: string;
		deliverycountry: string;
		contactpersonfirstname: string;
		contactpersonlastname: string;
		phone: string;
		agreetocreditcheck: boolean;
	};
	init: () => void;
	apply: () => void;
	validate: () => boolean;
	loadCountries: () => Promise<void>;
	addErrors: (errors: SubmissionError[] | ValidationFailure[] | ValidationFailure | string | null | undefined) => void;
}>;

const DEFAULT_COUNTRIES: CountryOption[] = [
	{ text: "Sweden", value: "SE" },
	{ text: "Norway", value: "NO" },
	{ text: "Denmark", value: "DK" },
	{ text: "Finland", value: "FI" },
	{ text: "Germany", value: "DE" },
	{ text: "Netherlands", value: "NL" },
	{ text: "United Kingdom", value: "GB" },
	{ text: "United States", value: "US" },
];

const DEFAULT_BUSINESS_AREAS: BusinessAreaOption[] = [
	{ text: "Climate Screens", value: "CS" },
	{ text: "Interior Textiles", value: "IT" },
];

const DEFAULT_CUSTOMER_ROLES: Record<string, RoleOption[]> = {
	IT: [
		{ text: "IT1", value: "IT1" },
		{ text: "IT2", value: "IT2" },
		{ text: "IT3", value: "IT3" },
		{ text: "IT4", value: "IT4" },
		{ text: "IT5", value: "IT5" },
		{ text: "IT6", value: "IT6" },
		{ text: "IT7", value: "IT7" },
		{ text: "IT8", value: "IT8" },
		{ text: "IT9", value: "IT9" },
		{ text: "IT10", value: "IT10" },
		{ text: "IT11", value: "IT11" },
		{ text: "IT12", value: "IT12" },
	],
	CS: [
		{ text: "CS1", value: "CS1" },
		{ text: "CS2", value: "CS2" },
		{ text: "CS3", value: "CS3" },
		{ text: "CS4", value: "CS4" },
		{ text: "CS5", value: "CS5" },
		{ text: "CS6", value: "CS6" },
		{ text: "CS7", value: "CS7" },
		{ text: "CS8", value: "CS8" },
		{ text: "CS9", value: "CS9" },
		{ text: "CS10", value: "CS10" },
		{ text: "CS11", value: "CS11" },
		{ text: "CS12", value: "CS12" },
	],
};

const readEmailFromQuery = () => {
	if (typeof window === "undefined") {
		return "";
	}
	const params = new URLSearchParams(window.location.search);
	return (params.get("email") ?? "").trim();
};

export default (config?: RegisterFormConfig): RegisterFormComponent => {
	const resolvedConfig = config ?? {};
	return {
		applicationSubmitted: false,
		applicationError: false,
		isSubmitting: false,
		submissionErrors: [],
		termsAccepted: false,
		gdprAccepted: false,
		countries: resolvedConfig.countries ?? DEFAULT_COUNTRIES,
		businessAreas: resolvedConfig.businessAreas ?? DEFAULT_BUSINESS_AREAS,
		customerRoles: resolvedConfig.customerRoles ?? DEFAULT_CUSTOMER_ROLES,
		submitText: resolvedConfig.submitText ?? "Apply",
		submittingText: resolvedConfig.submittingText ?? "Submitting...",
		validationFailedText: resolvedConfig.validationFailedText ?? "Please fill in required fields.",
		genericErrorText: resolvedConfig.genericErrorText ?? "Something went wrong. Please try again later.",
		newCustomer: {
			email: "",
			selectedbusinessarea: "",
			selectedcustomerrole: "",
			companyname: "",
			companyvatnumber: "",
			postaladdress1: "",
			postaladdress2: "",
			postalzipcode: "",
			postalcity: "",
			postalcountry: "",
			usedifferentdeliveryaddress: false,
			deliveryaddress1: "",
			deliveryaddress2: "",
			deliveryzipcode: "",
			deliverycity: "",
			deliverycountry: "",
			contactpersonfirstname: "",
			contactpersonlastname: "",
			phone: "",
			agreetocreditcheck: false,
		},
		init() {
			this.newCustomer.email = readEmailFromQuery();
			this.loadCountries();
		},
		validate() {
			this.submissionErrors = [];
			let hasErrors = false;

			if (!this.newCustomer.email) {
				hasErrors = true;
			}
			if (!this.newCustomer.selectedbusinessarea) {
				hasErrors = true;
			}
			if (!this.newCustomer.selectedcustomerrole) {
				hasErrors = true;
			}
			if (!this.newCustomer.companyname) {
				hasErrors = true;
			}
			if (!this.newCustomer.postaladdress1) {
				hasErrors = true;
			}
			if (!this.newCustomer.postalzipcode) {
				hasErrors = true;
			}
			if (!this.newCustomer.postalcity) {
				hasErrors = true;
			}
			if (!this.newCustomer.postalcountry) {
				hasErrors = true;
			}
			if (!this.newCustomer.contactpersonfirstname) {
				hasErrors = true;
			}
			if (!this.newCustomer.contactpersonlastname) {
				hasErrors = true;
			}
			if (!this.newCustomer.phone) {
				hasErrors = true;
			}

			if (this.newCustomer.usedifferentdeliveryaddress) {
				if (!this.newCustomer.deliveryaddress1) {
					hasErrors = true;
				}
				if (!this.newCustomer.deliveryzipcode) {
					hasErrors = true;
				}
				if (!this.newCustomer.deliverycity) {
					hasErrors = true;
				}
				if (!this.newCustomer.deliverycountry) {
					hasErrors = true;
				}
			}

			if (!this.termsAccepted) {
				hasErrors = true;
			}
			if (!this.gdprAccepted) {
				hasErrors = true;
			}

			if (hasErrors) {
				this.submissionErrors.push({ errorMessage: this.validationFailedText });
			}

			return !hasErrors;
		},
		async loadCountries() {
			const culture = document.documentElement.lang || undefined;
			const response = await client.get<CountryOption[]>({
				url: "/_esfn/api/Account/countries",
				query: { culture },
			});

			if (response.response.ok && response.data?.length) {
				this.countries = response.data;
				return;
			}

			if (this.countries.length === 0) {
				this.countries = DEFAULT_COUNTRIES;
			}
		},
		async apply() {
			if (!this.validate()) {
				return;
			}

			this.isSubmitting = true;
			this.applicationError = false;
			this.applicationSubmitted = false;

			const culture = document.documentElement.lang || undefined;

			const response = await client.post<RegisterNewCustomerResponses, RegisterNewCustomerErrors>({
				url: "/_esfn/api/Account/apply",
				headers: {
					"Content-Type": "application/json",
				},
				body: {
					culture,
					customer: this.newCustomer,
				},
			});

			this.isSubmitting = false;

			if (!response.response.ok) {
				this.addErrors(response.error);
				this.applicationError = true;
				return;
			}

			if (response.data?.success) {
				this.applicationSubmitted = true;
				return;
			}

			this.applicationError = true;
		},
		addErrors(errors) {
			if (!errors) {
				this.submissionErrors.push({ errorMessage: this.genericErrorText });
				return;
			}

			const normalized = Array.isArray(errors) ? errors : [errors];
			for (const error of normalized) {
				if (typeof error === "string") {
					this.submissionErrors.push({ errorMessage: error });
					continue;
				}

				const message = error?.errorMessage ?? this.genericErrorText;
				this.submissionErrors.push({ errorMessage: message });
			}
		},
	};
};
